import { useState, useEffect } from 'react';
import { Lock, ChevronDown, Check, ShoppingBag, AlertCircle, CreditCard, Banknote, ExternalLink, Info, Home, Truck, Plus, CheckCircle2 } from 'lucide-react';

const cur  = () => window.themeData?.currency ?? '$';
const td   = () => window.themeData ?? {};
const base = () => (td().apiUrl ?? '').replace(/\/$/, '');

// ── Shared UI ─────────────────────────────────────────────────────────────────

function Field({ label, type = 'text', placeholder, value, onChange, required }) {
  return (
    <div>
      <label className="block text-sm font-bold text-gray-700 mb-1.5">
        {label}{required && <span className="text-red-400 ml-1">*</span>}
      </label>
      <input
        type={type} value={value} placeholder={placeholder} required={required}
        onChange={e => onChange(e.target.value)}
        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-textDark text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
      />
    </div>
  );
}

// ── Saved address picker ───────────────────────────────────────────────────────
function SavedAddressPicker({ addresses, selectedKey, onSelect, onUseNew }) {
  return (
    <div className="mb-6">
      <p className="text-sm font-bold text-gray-500 mb-3">Choose a saved address or enter a new one</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        {addresses.map(addr => {
          const selected = selectedKey === addr.key;
          const lines = [addr.address_1, addr.address_2, [addr.city, addr.state, addr.postcode].filter(Boolean).join(', '), addr.country].filter(Boolean);
          return (
            <button
              key={addr.key} type="button"
              onClick={() => onSelect(addr.key)}
              className={`text-left p-4 rounded-2xl border-2 transition-all w-full ${
                selected
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 hover:border-primary/40 bg-gray-50 hover:bg-white'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  {addr.key === 'billing'
                    ? <Home className="w-4 h-4 text-primary flex-shrink-0" />
                    : <Truck className="w-4 h-4 text-accent2 flex-shrink-0" />
                  }
                  <span className="text-xs font-black uppercase tracking-wide text-gray-500">
                    {addr.key === 'billing' ? 'Billing Address' : 'Shipping Address'}
                  </span>
                </div>
                {selected && <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />}
              </div>
              <p className="font-bold text-sm text-textDark">{addr.first_name} {addr.last_name}</p>
              {lines.map((l, i) => (
                <p key={i} className="text-xs text-gray-500 mt-0.5">{l}</p>
              ))}
            </button>
          );
        })}

        <button
          type="button" onClick={onUseNew}
          className={`text-left p-4 rounded-2xl border-2 transition-all w-full ${
            selectedKey === 'new'
              ? 'border-primary bg-primary/5'
              : 'border-dashed border-gray-300 hover:border-primary/40 bg-white'
          }`}
        >
          <div className="flex items-center gap-2 mb-1">
            <Plus className={`w-4 h-4 ${selectedKey === 'new' ? 'text-primary' : 'text-gray-400'}`} />
            <span className={`text-xs font-black uppercase tracking-wide ${selectedKey === 'new' ? 'text-primary' : 'text-gray-400'}`}>
              Enter New Address
            </span>
            {selectedKey === 'new' && <CheckCircle2 className="w-4 h-4 text-primary ml-auto" />}
          </div>
          <p className="text-xs text-gray-400 mt-1">Fill in details below</p>
        </button>
      </div>
    </div>
  );
}

function Select({ label, value, onChange, options, required }) {
  return (
    <div>
      <label className="block text-sm font-bold text-gray-700 mb-1.5">
        {label}{required && <span className="text-red-400 ml-1">*</span>}
      </label>
      <div className="relative">
        <select
          value={value} onChange={e => onChange(e.target.value)} required={required}
          className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-textDark text-sm focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all appearance-none cursor-pointer"
        >
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  );
}

function GatewayIcon({ gw }) {
  if (gw.icon) return <img src={gw.icon} alt={gw.title} className="h-5 w-auto object-contain flex-shrink-0 max-w-[56px]" />;
  if (gw.type === 'offline')  return <Banknote     className="w-4 h-4 text-accent3 flex-shrink-0" />;
  if (gw.type === 'redirect') return <CreditCard   className="w-4 h-4 text-primary flex-shrink-0" />;
  return <ExternalLink className="w-4 h-4 text-accent2 flex-shrink-0" />;
}

export default function CheckoutPage() {
  const [cartItems,     setCartItems]     = useState([]);
  const [cartTotals,    setCartTotals]    = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [savedAddrs,    setSavedAddrs]    = useState([]);   // [{key,first_name,...}]
  const [selectedAddr,  setSelectedAddr]  = useState(null); // 'billing' | 'shipping' | 'new' | null
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', phone: '',
    address: '', city: '', state: '', zip: '', country: 'IN',
  });
  const [payMethod,  setPayMethod]  = useState('');
  const [orderError, setOrderError] = useState('');
  const [placing,    setPlacing]    = useState(false);

  const isLoggedIn    = !!(td().isLoggedIn);
  const gateways      = td().paymentGateways ?? [];
  const countryStates = (td().wcStates ?? {})[form.country] ?? null;
  const selGw         = gateways.find(g => g.id === payMethod) ?? gateways[0];
  const HDRS          = { 'Content-Type': 'application/json', 'Nonce': td().wcStoreNonce ?? '' };

  // Reset state when country changes
  const set = key => val => setForm(f => ({
    ...f, [key]: val,
    ...(key === 'country' ? { state: '' } : {}),
  }));

  // Fill form from a saved address object
  function applyAddress(addr) {
    setForm(f => ({
      ...f,
      firstName: addr.first_name  ?? f.firstName,
      lastName:  addr.last_name   ?? f.lastName,
      email:     addr.email       ?? f.email,
      phone:     addr.phone       ?? f.phone,
      address:   addr.address_1   ?? f.address,
      city:      addr.city        ?? f.city,
      state:     addr.state       ?? f.state,
      zip:       addr.postcode    ?? f.zip,
      country:   addr.country     ?? f.country,
    }));
  }

  function handleSelectAddr(key) {
    setSelectedAddr(key);
    const addr = savedAddrs.find(a => a.key === key);
    if (addr) applyAddress(addr);
  }

  function handleUseNew() {
    setSelectedAddr('new');
    setForm({ firstName: '', lastName: '', email: td().userEmail ?? '', phone: '', address: '', city: '', state: '', zip: '', country: 'IN' });
  }

  useEffect(() => {
    const STORE_HDRS = { 'Content-Type': 'application/json', 'Nonce': td().wcStoreNonce ?? '' };
    fetch(`${base()}/wc/store/v1/cart`, { credentials: 'same-origin', headers: STORE_HDRS })
      .then(r => r.json())
      .then(data => {
        setCartItems((data.items ?? []).map(item => ({
          id:    item.id,
          key:   item.key,
          name:  item.name,
          image: item.images?.[0]?.src ?? null,
          price: parseFloat(item.prices?.price ?? item.price ?? 0) / 100,
          qty:   item.quantity,
        })));
        setCartTotals(data.totals ?? null);
      })
      .catch(() => setCartItems([]))
      .finally(() => setLoading(false));

    if (gateways.length) setPayMethod(gateways[0].id);

    // Load saved addresses if logged in
    if (isLoggedIn) {
      fetch(`${base()}/little-anvi/v1/account`, {
        credentials: 'same-origin',
        headers: { 'X-WP-Nonce': td().nonce ?? '' },
      })
        .then(r => r.json())
        .then(account => {
          const addrs = [];
          const billing  = account.billing  ?? {};
          const shipping = account.shipping ?? {};
          if (billing.address_1)  addrs.push({ key: 'billing',  ...billing  });
          if (shipping.address_1) addrs.push({ key: 'shipping', ...shipping });
          setSavedAddrs(addrs);
          // Auto-select shipping if available, else billing
          if (addrs.length) {
            const preferred = addrs.find(a => a.key === 'shipping') ?? addrs[0];
            setSelectedAddr(preferred.key);
            applyAddress({ ...preferred, email: preferred.email ?? account.email ?? '' });
          }
        })
        .catch(() => {});
    }
  }, []);

  // When state/country changes → update WC cart session so taxes are recalculated
  useEffect(() => {
    if (!form.country) return;
    const STORE_HDRS = { 'Content-Type': 'application/json', 'Nonce': td().wcStoreNonce ?? '' };
    fetch(`${base()}/wc/store/v1/cart/update-customer`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: STORE_HDRS,
      body: JSON.stringify({
        billing_address:  { country: form.country, state: form.state || '' },
        shipping_address: { country: form.country, state: form.state || '' },
      }),
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data?.totals) setCartTotals(data.totals); })
      .catch(() => {});
  }, [form.state, form.country]);

  const div      = Math.pow(10, cartTotals?.currency_minor_unit ?? 2);
  const subtotal = cartItems.reduce((s, i) => s + i.price * i.qty, 0);
  const tax      = cartTotals ? parseFloat(cartTotals.total_tax ?? 0) / div : 0;
  const rawTaxLines   = (cartTotals?.tax_lines ?? []).filter(tl => parseFloat(tl.tax_total ?? 0) > 0);
  const GST_SPECIFIC  = ['CGST', 'SGST', 'IGST'];
  const alreadySplit  = rawTaxLines.length >= 2 ||
    (rawTaxLines.length === 1 && GST_SPECIFIC.includes((rawTaxLines[0]?.name ?? '').toUpperCase()));
  const taxLines = alreadySplit ? rawTaxLines : null;
  const storeState   = (window.themeData?.storeState ?? 'TN').toUpperCase();
  const isIntraState = form.state && form.state.toUpperCase() === storeState;
  const shipping = subtotal >= 1999 ? 0 : 70;
  const total    = subtotal + tax + shipping;

  const billing  = () => ({
    first_name: form.firstName, last_name: form.lastName,
    email: form.email, phone: form.phone,
    address_1: form.address, city: form.city,
    state: form.state, postcode: form.zip, country: form.country,
  });
  const shipAddr = () => ({
    first_name: form.firstName, last_name: form.lastName,
    address_1: form.address, city: form.city,
    state: form.state, postcode: form.zip, country: form.country,
  });

  async function handleSubmit(e) {
    e.preventDefault();
    setPlacing(true);
    setOrderError('');

    try {
      const gw = selGw ?? gateways[0];

      // ── OFFLINE (COD / BACS / Cheque) → direct WC Store API ──────────────
      if (gw?.type === 'offline') {
        // Inject shipping fee into WC cart session before checkout
        await fetch(`${base()}/little-anvi/v1/set-shipping-fee`, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': td().nonce ?? '' },
          body: JSON.stringify({ amount: shipping }),
        }).catch(() => {});

        const res  = await fetch(`${base()}/wc/store/v1/checkout`, {
          method: 'POST', credentials: 'same-origin', headers: HDRS,
          body: JSON.stringify({
            billing_address:  billing(),
            shipping_address: shipAddr(),
            payment_method:   payMethod,
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          setOrderError(data?.message ?? 'Order failed. Please try again.');
          setPlacing(false);
          return;
        }
        window.location.href = data.payment_result?.redirect_url
          ?? `${td().siteUrl ?? ''}/checkout/order-received/${data.order_id}/`;
        return;
      }

      // ── ALL OTHER GATEWAYS (Stripe, PayPal, PayU, etc.)
      //    → pre-fill WC session → redirect to WC native checkout ────────────
      const sessionRes  = await fetch(`${base()}/little-anvi/v1/set-checkout-session`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': td().nonce ?? '' },
        body: JSON.stringify({
          billing:         billing(),
          shipping:        shipAddr(),
          payment_method:  payMethod,
          items:           cartItems.map(item => ({ id: item.id, qty: item.qty })),
          shipping_total:  shipping,
        }),
      });
      const sessionData = await sessionRes.json();
      if (!sessionRes.ok) {
        setOrderError(sessionData?.message ?? 'Could not start checkout. Please try again.');
        setPlacing(false);
        return;
      }
      window.location.href = sessionData.checkout_url;

    } catch {
      setOrderError('Something went wrong. Please try again.');
      setPlacing(false);
    }
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-[50vh]">
        <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-textLight font-bold">Loading checkout…</p>
      </div>
    );
  }

  if (!cartItems.length) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 flex flex-col items-center text-center gap-4">
        <ShoppingBag className="w-16 h-16 text-primary" />
        <h2 className="text-2xl font-black text-textDark">Your cart is empty</h2>
        <a href={`${td().siteUrl ?? ''}/shop/`} className="bg-primary text-white font-bold px-8 py-3 rounded-2xl hover:bg-primaryHover transition-colors">
          Shop Now
        </a>
      </div>
    );
  }

  if (!gateways.length) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 flex flex-col items-center text-center gap-4">
        <AlertCircle className="w-12 h-12 text-yellow-400" />
        <h2 className="text-xl font-black text-textDark">No payment methods available</h2>
        <p className="text-textLight">Enable payment methods in <strong>WooCommerce → Settings → Payments</strong>.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-28 pt-8">

      {/* Mobile sticky CTA */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-100 z-50 shadow-[0_-4px_10px_rgba(0,0,0,0.06)]">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-bold text-gray-500">Total</span>
          <span className="text-xl font-black text-primary">{cur()}{total.toFixed(2)}</span>
        </div>
        <button form="checkout-form" type="submit" disabled={placing}
          className="w-full bg-primary hover:bg-primaryHover text-white font-black text-lg py-4 rounded-xl transition-all flex items-center justify-center gap-2 disabled:opacity-60">
          {placing ? 'Processing…' : selGw?.type === 'redirect' ? 'Continue to Payment' : 'Place Order'}
          <Check className="w-5 h-5" />
        </button>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-black text-textDark">Checkout</h1>
        <div className="flex items-center gap-2 bg-green-50 text-green-600 px-4 py-2 rounded-xl text-sm font-bold border border-green-100">
          <Lock className="w-4 h-4" /> Secure 256-bit SSL Encryption
        </div>
      </div>

      <form id="checkout-form" onSubmit={handleSubmit}>
        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start">

          {/* ── LEFT ─────────────────────────────────────────────────────────── */}
          <div className="w-full lg:w-2/3 flex flex-col gap-8">

            {/* 1. Shipping address */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 md:p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center font-black text-lg">1</div>
                <h2 className="text-xl md:text-2xl font-bold text-textDark">Shipping Address</h2>
              </div>

              {savedAddrs.length > 0 && (
                <SavedAddressPicker
                  addresses={savedAddrs}
                  selectedKey={selectedAddr}
                  onSelect={handleSelectAddr}
                  onUseNew={handleUseNew}
                />
              )}

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="First Name" placeholder="Jane"  value={form.firstName} onChange={set('firstName')} required />
                  <Field label="Last Name"  placeholder="Doe"   value={form.lastName}  onChange={set('lastName')}  required />
                </div>
                <Field label="Email Address" type="email" placeholder="jane@example.com" value={form.email}   onChange={set('email')}   required />
                <Field label="Phone Number"  type="tel"  placeholder="+91 98765 43210"   value={form.phone}   onChange={set('phone')} required />
                <Field label="Street Address"              placeholder="123 Main St"      value={form.address} onChange={set('address')} required />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Field label="City" placeholder="Chennai" value={form.city} onChange={set('city')} required />

                  {/* State — dropdown when WC has state codes, free text otherwise */}
                  {countryStates ? (
                    <Select
                      label="State" value={form.state} onChange={set('state')} required
                      options={[
                        { value: '', label: 'Select state…' },
                        ...Object.entries(countryStates).map(([code, name]) => ({ value: code, label: name })),
                      ]}
                    />
                  ) : (
                    <Field label="State" placeholder="State" value={form.state} onChange={set('state')} required />
                  )}

                  <Field label="ZIP / Post" placeholder="600001" value={form.zip} onChange={set('zip')} required />
                </div>
                <Select
                  label="Country" value={form.country} onChange={set('country')} required
                  options={[
                    { value: 'IN', label: 'India' },
                    { value: 'US', label: 'United States' },
                    { value: 'GB', label: 'United Kingdom' },
                    { value: 'AU', label: 'Australia' },
                    { value: 'CA', label: 'Canada' },
                    { value: 'SG', label: 'Singapore' },
                    { value: 'AE', label: 'United Arab Emirates' },
                    { value: 'DE', label: 'Germany' },
                    { value: 'FR', label: 'France' },
                  ]}
                />
              </div>
            </div>

            {/* 2. Payment method */}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 md:p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-10 h-10 bg-primary/10 text-primary rounded-full flex items-center justify-center font-black text-lg">2</div>
                <h2 className="text-xl md:text-2xl font-bold text-textDark">Payment Method</h2>
              </div>

              <div className="space-y-3">
                {gateways.map(gw => (
                  <label
                    key={gw.id}
                    className={`flex items-center gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-all ${payMethod === gw.id ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-primary/40'}`}
                  >
                    <input
                      type="radio" name="payMethod" value={gw.id} checked={payMethod === gw.id}
                      onChange={() => { setPayMethod(gw.id); setOrderError(''); }}
                      className="accent-primary w-5 h-5 flex-shrink-0"
                    />
                    <GatewayIcon gw={gw} />
                    <span className="font-bold text-textDark text-sm flex-1">{gw.title}</span>
                  </label>
                ))}
              </div>

              {/* Payment info panel */}
              {selGw?.type === 'redirect' && (
                <div className="mt-4 bg-blue-50 border border-blue-100 rounded-2xl p-4 flex items-start gap-3">
                  <Info className="w-4 h-4 text-accent2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-700 font-semibold">
                    {selGw.description || `Click "Continue to Payment" — you'll be taken to ${selGw.title}'s secure payment page.`}
                  </p>
                </div>
              )}
              {selGw?.type === 'offline' && selGw.description && (
                <div className="mt-4 bg-gray-50 border border-gray-100 rounded-2xl p-4 text-sm text-gray-600 font-semibold">
                  {selGw.description}
                </div>
              )}

              {orderError && (
                <div className="mt-4 flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl p-4 font-semibold">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />{orderError}
                </div>
              )}
            </div>
          </div>

          {/* ── RIGHT: order summary ──────────────────────────────────────────── */}
          <div className="w-full lg:w-1/3 lg:sticky lg:top-28 self-start mb-24 lg:mb-0">
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 md:p-8">
              <h2 className="text-2xl font-black text-textDark mb-6">Order Summary</h2>

              <div className="max-h-64 overflow-y-auto space-y-3 mb-6 pr-1">
                {cartItems.map(item => (
                  <div key={item.key} className="flex items-center gap-3">
                    <div className="w-14 h-14 bg-gray-50 rounded-xl flex-shrink-0 flex items-center justify-center overflow-hidden border border-gray-100">
                      {item.image
                        ? <img src={item.image} alt={item.name} className="w-full h-full object-contain" />
                        : <span className="text-2xl">🎁</span>
                      }
                    </div>
                    <div className="flex-grow min-w-0">
                      <p className="font-bold text-sm text-textDark line-clamp-1">{item.name}</p>
                      <p className="text-xs text-gray-400">Qty: {item.qty}</p>
                    </div>
                    <span className="font-black text-sm text-primary flex-shrink-0">{cur()}{(item.price * item.qty).toFixed(2)}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-100 pt-4 space-y-2.5 mb-6">
                <div className="flex justify-between text-sm font-bold text-gray-600">
                  <span>Subtotal</span><span>{cur()}{subtotal.toFixed(2)}</span>
                </div>
                {tax > 0 && (
                  taxLines
                    ? taxLines.map((tl, i) => (
                        <div key={i} className="flex justify-between text-sm font-bold text-gray-600">
                          <span>{tl.name}</span>
                          <span>{cur()}{(parseFloat(tl.tax_total) / div).toFixed(2)}</span>
                        </div>
                      ))
                    : isIntraState
                      ? <>
                          <div className="flex justify-between text-sm font-bold text-gray-600">
                            <span>CGST</span><span>{cur()}{(tax / 2).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm font-bold text-gray-600">
                            <span>SGST</span><span>{cur()}{(tax / 2).toFixed(2)}</span>
                          </div>
                        </>
                      : <div className="flex justify-between text-sm font-bold text-gray-600">
                          <span>IGST</span><span>{cur()}{tax.toFixed(2)}</span>
                        </div>
                )}
                <div className="flex justify-between text-sm font-bold text-gray-600">
                  <span>Shipping</span>
                  <span className={shipping === 0 ? 'text-accent3' : ''}>{shipping === 0 ? 'FREE 🎉' : `${cur()}${shipping}`}</span>
                </div>
                <div className="flex justify-between items-baseline pt-3 border-t border-gray-100">
                  <span className="font-black text-lg text-textDark">Total</span>
                  <span className="font-black text-3xl text-primary">{cur()}{total.toFixed(2)}</span>
                </div>
              </div>

              <button
                type="submit" disabled={placing}
                className="hidden lg:flex w-full bg-primary hover:bg-primaryHover text-white font-black text-lg py-4 px-6 rounded-2xl transition-all items-center justify-center gap-2 disabled:opacity-60"
              >
                {placing ? 'Processing…' : selGw?.type === 'redirect' ? 'Continue to Payment' : 'Place Order'}
                <Check className="w-5 h-5" />
              </button>

              <div className="mt-4 flex items-center justify-center gap-2 text-xs text-gray-400 font-bold">
                <Lock className="w-3 h-3" /> All payments are 100% secure
              </div>
            </div>
          </div>

        </div>
      </form>
    </div>
  );
}
